import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HelpCircle, Lock, UploadCloud } from "lucide-react";
import type { FormatHub } from "../content/formats";
import { FORMAT_HUBS } from "../content/formats";
import { ContextualLinks } from "../components/ContextualLinks";
import { useUi } from "../i18n";

export function FormatPage({ hub }: { hub: FormatHub }) {
  const { t } = useUi();
  const otherHubs = FORMAT_HUBS.filter((h) => h.slug !== hub.slug);

  return (
    <article className="panel legal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <header style={{ marginBottom: 24 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <Lock size={12} /> {hub.formatName} Transfer Hub
        </span>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          {hub.h1}
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          {hub.lead}
        </p>
      </header>

      {/* Interactive Send Card (Theme-Adaptive) */}
      <div style={{ padding: 24, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 32, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8, color: "var(--lime)" }}>
          <UploadCloud size={24} />
          <b style={{ fontSize: 16 }}>{t.ctaReadyTitle} ({hub.formatName})</b>
        </div>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          {t.ctaReadySub}
        </p>
        <Link to="/" className="btn block" style={{ maxWidth: 360, margin: "0 auto", textDecoration: "none" }}>
          <span>{t.ctaStartBtn}</span> <ArrowRight size={16} />
        </Link>
      </div>

      <section style={{ marginBottom: 28 }}>
        <h2>{t.techSpecsTitle}</h2>
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Specification</th>
            </tr>
          </thead>
          <tbody>
            {hub.specs.map((s) => (
              <tr key={s.label}>
                <td style={{ fontWeight: 600 }}>{s.label}</td>
                <td>{s.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2>Why standard email fails for {hub.formatName}</h2>
        <p>{hub.whyEmailFails}</p>
        <div className="alert warn" style={{ marginTop: 12 }}>
          <b>{hub.comparisonTitle}:</b> {hub.comparisonNote}
        </div>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2>Best practices for sending {hub.formatName}</h2>
        <ul style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 0, listStyle: "none" }}>
          {hub.bestPractices.map((bp, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <CheckCircle2 size={16} color="#00ff66" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{bp}</span>
            </li>
          ))}
        </ul>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>Frequently asked questions</h2>
        {hub.faq.map((f, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 4px", fontSize: 15 }}>
              <HelpCircle size={15} color="var(--lime)" /> {f.q}
            </h3>
            <p className="hint" style={{ margin: 0 }}>{f.a}</p>
          </div>
        ))}
      </section>

      <ContextualLinks currentSlug={hub.slug} />
    </article>
  );
}
